/**
 * Product Tabs Section - JavaScript
 * Handles tab switching and accordion functionality
 *
 * Features:
 * - Desktop: Horizontal tab navigation
 * - Mobile: Accordion-style expandable sections
 * - Smooth transitions and animations
 * - Keyboard accessibility
 */

(function() {
  'use strict';

  /**
   * Debounce utility function
   */
  function debounce(func, wait) {
    let timeout;
    return function executedFunction() {
      const context = this;
      const args = arguments;
      const later = function() {
        timeout = null;
        func.apply(context, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * ProductTabsSection Class
   */
  class ProductTabsSection {
    constructor(element) {
      if (!element) {
        console.warn('ProductTabsSection: Element not found');
        return;
      }

      this.container = element;
      this.sectionId = this.container.dataset.sectionId || 'product-tabs';
      this.defaultOpenTab = parseInt(this.container.dataset.defaultOpen, 10) || 0;
      this.tabs = this.container.querySelectorAll('.tablinks-cus');
      this.tabContents = this.container.querySelectorAll('.tabcontent-cus');
      this.accordionHeaders = this.container.querySelectorAll('.accordion-header');
      this.isDesktop = window.innerWidth >= 961;
      this._boundResize = null;

      this.init();
    }

    init() {
      this.setupEventListeners();
      this.setupResponsiveHandler();
      this.setInitialState();
    }

    /**
     * Set up all event listeners
     */
    setupEventListeners() {
      const self = this;

      // Desktop tab clicks
      this.tabs.forEach(function(tab, index) {
        tab.addEventListener('click', function(e) {
          e.preventDefault();
          self.switchTab(index);
        });

        // Keyboard accessibility
        tab.addEventListener('keydown', function(e) {
          self.handleKeyboardNav(e, index);
        });
      });

      // Mobile accordion clicks
      this.accordionHeaders.forEach(function(header, index) {
        header.addEventListener('click', function(e) {
          e.preventDefault();
          self.toggleAccordion(index);
        });

        // Keyboard accessibility
        header.addEventListener('keydown', function(e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            self.toggleAccordion(index);
          }
        });
      });
    }

    /**
     * Set up responsive handler for device switching
     */
    setupResponsiveHandler() {
      const self = this;

      // Media query for desktop
      if (window.matchMedia) {
        const mediaQuery = window.matchMedia('(min-width: 961px)');
        mediaQuery.addEventListener('change', function(e) {
          self.handleMediaChange(e.matches);
        });
      }

      // Handle window resize with debounce
      this._boundResize = debounce(function() {
        self.handleResize();
      }, 100);

      window.addEventListener('resize', this._boundResize);
    }

    /**
     * Handle media query change
     */
    handleMediaChange(isDesktop) {
      const wasDesktop = this.isDesktop;
      this.isDesktop = isDesktop;

      // If switching from mobile to desktop, ensure correct tab is visible
      if (!wasDesktop && isDesktop) {
        this.syncDesktopAndMobile();
      }
    }

    /**
     * Sync desktop and mobile states when switching views
     */
    syncDesktopAndMobile() {
      const activeIndex = this.getActiveIndex();
      this.closeAllTabs();
      this.closeAllAccordions();
      this.setActiveTab(activeIndex);
    }

    /**
     * Set initial state based on default open tab
     */
    setInitialState() {
      if (this.isDesktop) {
        this.closeAllTabs();
        this.setActiveTab(this.defaultOpenTab);
      } else {
        this.closeAllAccordions();
        this.setActiveAccordion(this.defaultOpenTab);
      }
    }

    /**
     * Handle window resize
     */
    handleResize() {
      const newIsDesktop = window.innerWidth >= 961;
      if (newIsDesktop !== this.isDesktop) {
        this.handleMediaChange(newIsDesktop);
      }
    }

    /**
     * Switch to a specific tab
     */
    switchTab(index) {
      const self = this;

      // Remove active class from all tabs and content
      this.tabs.forEach(function(tab) {
        tab.classList.remove('active');
        tab.setAttribute('aria-selected', 'false');
        tab.setAttribute('tabindex', '-1');
      });

      this.tabContents.forEach(function(content) {
        content.classList.remove('active');
        content.setAttribute('aria-hidden', 'true');
      });

      // Add active class to clicked tab and corresponding content
      if (this.tabs[index]) {
        this.tabs[index].classList.add('active');
        this.tabs[index].setAttribute('aria-selected', 'true');
        this.tabs[index].setAttribute('tabindex', '0');
      }

      if (this.tabContents[index]) {
        this.tabContents[index].classList.add('active');
        this.tabContents[index].setAttribute('aria-hidden', 'false');
      }

      // Update URL hash for direct linking (optional)
      if (this.tabs[index] && this.tabs[index].dataset.tab) {
        const tabId = this.tabs[index].dataset.tab;
        history.replaceState(null, null, '#' + tabId);
      }

      // Dispatch custom event
      this.container.dispatchEvent(new CustomEvent('tab:change', {
        detail: { index: index, tabId: this.tabs[index] ? this.tabs[index].dataset.tab : null },
        bubbles: true
      }));
    }

    /**
     * Toggle accordion at specific index
     */
    toggleAccordion(index) {
      const header = this.accordionHeaders[index];
      const content = this.tabContents[index];
      const isActive = header && header.classList.contains('active');

      // Close all accordions first
      this.closeAllAccordions();

      // If the clicked one wasn't active, open it
      if (!isActive) {
        this.setActiveAccordion(index);
      }

      // Dispatch custom event
      this.container.dispatchEvent(new CustomEvent('accordion:toggle', {
        detail: { index: index, isOpen: !isActive },
        bubbles: true
      }));
    }

    /**
     * Set specific accordion as active
     */
    setActiveAccordion(index) {
      const header = this.accordionHeaders[index];
      const content = this.tabContents[index];

      if (header) {
        header.classList.add('active');
        header.setAttribute('aria-expanded', 'true');
      }

      if (content) {
        content.classList.add('active');
        content.setAttribute('aria-hidden', 'false');
      }
    }

    /**
     * Close all accordion panels
     */
    closeAllAccordions() {
      this.accordionHeaders.forEach(function(header) {
        header.classList.remove('active');
        header.setAttribute('aria-expanded', 'false');
      });

      this.tabContents.forEach(function(content) {
        content.classList.remove('active');
        content.setAttribute('aria-hidden', 'true');
      });
    }

    /**
     * Close all tabs
     */
    closeAllTabs() {
      this.tabs.forEach(function(tab) {
        tab.classList.remove('active');
        tab.setAttribute('aria-selected', 'false');
        tab.setAttribute('tabindex', '-1');
      });

      this.tabContents.forEach(function(content) {
        content.classList.remove('active');
        content.setAttribute('aria-hidden', 'true');
      });
    }

    /**
     * Set specific tab as active
     */
    setActiveTab(index) {
      if (this.tabs[index]) {
        this.tabs[index].classList.add('active');
        this.tabs[index].setAttribute('aria-selected', 'true');
        this.tabs[index].setAttribute('tabindex', '0');
      }

      if (this.tabContents[index]) {
        this.tabContents[index].classList.add('active');
        this.tabContents[index].setAttribute('aria-hidden', 'false');
      }
    }

    /**
     * Get currently active index
     */
    getActiveIndex() {
      let activeIndex = this.defaultOpenTab;

      this.tabs.forEach(function(tab, index) {
        if (tab.classList.contains('active')) {
          activeIndex = index;
        }
      });

      this.accordionHeaders.forEach(function(header, index) {
        if (header.classList.contains('active')) {
          activeIndex = index;
        }
      });

      return activeIndex;
    }

    /**
     * Handle keyboard navigation for accessibility
     */
    handleKeyboardNav(event, currentIndex) {
      const totalTabs = this.tabs.length;
      let newIndex = currentIndex;

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          newIndex = currentIndex > 0 ? currentIndex - 1 : totalTabs - 1;
          this.switchTab(newIndex);
          if (this.tabs[newIndex]) {
            this.tabs[newIndex].focus();
          }
          break;

        case 'ArrowRight':
          event.preventDefault();
          newIndex = currentIndex < totalTabs - 1 ? currentIndex + 1 : 0;
          this.switchTab(newIndex);
          if (this.tabs[newIndex]) {
            this.tabs[newIndex].focus();
          }
          break;

        case 'Home':
          event.preventDefault();
          this.switchTab(0);
          if (this.tabs[0]) {
            this.tabs[0].focus();
          }
          break;

        case 'End':
          event.preventDefault();
          this.switchTab(totalTabs - 1);
          if (this.tabs[totalTabs - 1]) {
            this.tabs[totalTabs - 1].focus();
          }
          break;

        case 'Enter':
        case ' ':
          event.preventDefault();
          this.switchTab(currentIndex);
          break;
      }
    }

    /**
     * Destroy the instance and remove event listeners
     */
    destroy() {
      // Remove resize listener
      if (this._boundResize) {
        window.removeEventListener('resize', this._boundResize);
      }

      // Remove all event listeners by cloning elements
      this.tabs.forEach(function(tab) {
        const clone = tab.cloneNode(true);
        tab.parentNode.replaceChild(clone, tab);
      });

      this.accordionHeaders.forEach(function(header) {
        const clone = header.cloneNode(true);
        header.parentNode.replaceChild(clone, header);
      });
    }
  }

  /**
   * Initialize all Product Tabs Sections on the page
   */
  function initProductTabs() {
    const tabSections = document.querySelectorAll('.product-tabs-section');

    tabSections.forEach(function(section) {
      // Skip if already initialized
      if (section.dataset.initialized === 'true') {
        return;
      }

      // Initialize the section
      new ProductTabsSection(section);
      section.dataset.initialized = 'true';
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductTabs);
  } else {
    // DOM already loaded, initialize immediately
    initProductTabs();
  }

  // Shopify AJAX navigation support
  document.addEventListener('shopify:section:load', function(event) {
    // Find and initialize tabs in the loaded section
    const sectionId = event.detail ? event.detail.sectionId : null;
    if (sectionId) {
      const section = document.querySelector('[data-section-id="' + sectionId + '"]');
      if (section && section.classList.contains('product-tabs-section')) {
        section.dataset.initialized = 'false';
        new ProductTabsSection(section);
        section.dataset.initialized = 'true';
      }
    } else {
      // Fallback: reinitialize all tabs
      setTimeout(initProductTabs, 100);
    }
  });

  // Re-initialize when product page variants change
  document.addEventListener('variant:change', function() {
    setTimeout(initProductTabs, 100);
  });

  // Handle Shopify theme editor events (safely)
  try {
    if (typeof window.theme !== 'undefined' && window.theme.EditMode) {
      document.addEventListener('shopify:section:select', function() {
        setTimeout(initProductTabs, 100);
      });
    }
  } catch (e) {
    // Silently ignore - theme might not have EditMode
  }

  // Export for external use
  window.ProductTabsSection = ProductTabsSection;
  window.initProductTabs = initProductTabs;

})();
